import { Observable } from 'rxjs';
import { Parameter } from '../common/parameter';

export interface PackageMasterParameter extends Parameter {
  searchTerm: number;
  orderBy: string;
}

export interface PackageMaster {
  packageId?: number;
  packageCode: string;
  // Edit model (getById) name fields.
  packageNameEnglish: string;
  packageNameArabic: string;
  // List endpoint name fields (getPackageDetails).
  packageNameEn?: string;
  packageNameAr?: string;
  // Glossary codeValue (PACKAGETYPE); backend column is PackageTypeId (int). Sent on create/update.
  packageTypeId: number;
  // Display name of the package type, returned by the list endpoint (read-only).
  packageType?: string;
  // departmentId?: number | null;
  departmentId: string;
  departmentName?: string;
  packageDays: number;
  // Edit model (getById) flag; list endpoint uses isAuthorizationRequired.
  authorizationRequired: boolean;
  isAuthorizationRequired?: boolean;
  // Calculated (base cash price). DB column is NOT NULL, so always sent on POST/UPDATE.
  creditAmount: number;
  // list-view server aggregates
  totalIncludedAmount?: number;
  categoryCount?: number;
  serviceCount?: number;
  bedTypeCount?: number;
  insurerCount?: number;
  cashBasePrice?: number;
  insuranceBasePrice?: number;
  // Name arrays returned directly by the list endpoint (getPackageDetails)
  // alongside the counts. Displayed on the grid when present; the list falls
  // back to '-' when the backend has not (yet) supplied them.
  categoryNames?: string[];
  serviceNames?: string[];
  bedTypeNames?: string[];
  insurerNames?: string[];
  status: string;
  description?: string;
  tenantId?: any;
  branchId?: any;
  categories?: any[];
  // PackagePrice rows for the Cash context (PackageInsuranceMapId NULL)
  cashBedPrices?: PackageBedPrice[];
  // PackageInsuranceMap rows; each carries its own negotiated bedPrices[]
  insuranceMappings?: PackageInsuranceMap[];
  // PackageProcedureMapping_Det rows — Primary Service Mapping
  primaryServiceMappings?: PackagePrimaryService[];
  // server-computed on GETBYID
  summary?: PackageSummary;
  // audit
  // createdBy?: any;
  createdBy?: number;
  createdDate?: string;
  // updatedBy?: any;
  updatedBy?: number;
  updatedDate?: string;
  parameters?: PackageMasterParameter[];
  links?: any;
}

/** One PackagePrice row: (Package, BedType, Context). */
export interface PackageBedPrice {
  packagePriceId?: number | null;
  // Bed type = Ward; ward id is a GUID (string) coming from the Ward API
  // bedTypeId: string | number;
  bedTypeId: string; // GUID
  bedTypeName?: string;
  price: number;
  cost: number;
  margin?: number;
  remarks?: string;
}

export interface PackagePrimaryService {
  packageProcedureMappingId?: number | null;
  // Service id is a GUID (string) coming from the Service API
  serviceId: string;
  serviceCode?: string;
  serviceName?: string;
  displayOrder?: number;
  isActive?: boolean;
}

export interface PackageInsuranceMap {
  packageInsuranceMapId?: number | null;
  // Payer id is a GUID (string) coming from the Payers API
  // insuranceCompanyId: string | number;
  insuranceCompanyId: string; // GUID
  insuranceCompanyName?: string;
  contractId?: number | null;
  contractName?: string;
  policyId?: number | null;
  policyName?: string;
  covered: boolean;
  priorAuthRequired: boolean;
  status: string;
  bedPrices?: PackageBedPrice[];
  // Optional audit columns (nullable in DB)
  updatedBy?: number | null;
  updatedDate?: string | null;
}

/**
 * Exact create/update request contract (createPackageDetails / updatePackageDetails).
 * The backend DTO uses different field names than the read model (PackageMaster),
 * so buildPayload() maps the component view-model to this shape.
 * tenantId/branchId are NOT part of the body — the endpoint reads them from the URL.
 */
export interface PackageCategoryServicePayload {
  packageCategoryServiceId: number | null;
  serviceId: string;
  quantity: number;
  servicePrice: number;
  isMandatory: boolean;
  isIncluded: boolean;
  remarks: string;
}

export interface PackageCategoryPayload {
  packageCategoryId: number | null;
  serviceCategoryId: string;
  includedAmount: number;
  isIncluded: boolean;
  displayOrder: number;
  remarks: string;
  services: PackageCategoryServicePayload[];
}

export interface PackagePrimaryServicePayload {
  packageProcedureMappingId: number | null;
  serviceId: string;
  displayOrder: number;
  isActive: boolean;
  createdBy?: number;
  createdDate?: string;
}

export interface PackageInsurancePayload {
  packageInsuranceMapId: number | null;
  insuranceCompanyId: string;
  contractId?: number | null;
  policyId?: number | null;
  isCovered: boolean;
  isPriorAuthRequired: boolean;
  status: string;
  createdBy?: number;
  createdDate?: string;
  bedPrices: PackageBedPrice[];
}

export interface PackageMasterPayload {
  packageId?: number;
  packageCode: string;
  packageNameEn: string;
  packageNameAr: string;
  packageTypeId: number;
  departmentId: string;
  packageDays: number;
  isAuthorizationRequired: boolean;
  creditAmount: number;
  status: string;
  description?: string;
  createdBy?: number;
  createdDate?: string;
  cashBedPrices: PackageBedPrice[];
  categories: PackageCategoryPayload[];
  insuranceDetails: PackageInsurancePayload[];
  primaryServiceMappings: PackagePrimaryServicePayload[];
}

export interface PackageSummary {
  totalCategories?: number;
  totalServices?: number;
  servicesStandardTotal?: number;
  cashBasePrice?: number;
  cashBaseMargin?: number;
  insuranceBasePrice?: number;
  insuranceBaseMargin?: number;
}

export abstract class PackageMasterData {

  abstract list(url: string): Observable<PackageMaster[]>;

  abstract getById(url: string): Observable<PackageMaster>;

  abstract create(url: string, data: PackageMasterPayload): Observable<any>;

  abstract update(url: string, data: PackageMasterPayload): Observable<any>;

  abstract delete(url: string): Observable<any>;

}